import { Card, CardContent, CardHeader, CardTitle } from '@client/components/ui/card';
import { Badge } from '@client/components/ui/badge';
import { Separator } from '@client/components/ui/separator';
import { 
  Calendar, 
  User, 
  Hash, 
  TrendingUp,
  Building2,
  Users2,
  CalendarDays
} from 'lucide-react';
import type { List } from '@shared/types';
import { ListMetadata } from '@/client/types';

interface ListMetadataDisplayProps {
  list: List;
  variant?: 'full' | 'compact';
}

/**
 * List Metadata Display Component
 * Zeigt strukturierte Metadata-Informationen
 */
export function ListMetadataDisplay({ 
  list, 
  variant = 'full' 
}: ListMetadataDisplayProps) {
  const metadata: ListMetadata = list.metadata || {};
  
  if (variant === 'compact') {
    return (
      <div className="space-y-2 text-sm">
        {metadata.scrapedAt && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Calendar className="h-4 w-4" />
            <span>Scraped {formatRelativeDate(metadata.scrapedAt)}</span>
          </div>
        )}
        
        {metadata.sourceContext && (
          <div className="flex items-center gap-2">
            <Badge variant="outline">{metadata.sourceContext}</Badge>
            {metadata.scrapingMode && (
              <Badge variant="secondary">{metadata.scrapingMode}</Badge>
            )}
          </div>
        )}
        
        {(metadata.groupName || metadata.companyName || metadata.eventName) && (
          <div className="flex items-center gap-2 text-muted-foreground">
            {metadata.groupName && (
              <>
                <Users2 className="h-4 w-4" />
                <span>{metadata.groupName}</span>
              </>
            )}
            {metadata.companyName && (
              <>
                <Building2 className="h-4 w-4" />
                <span>{metadata.companyName}</span>
              </>
            )}
            {metadata.eventName && (
              <>
                <CalendarDays className="h-4 w-4" />
                <span>{metadata.eventName}</span>
              </>
            )}
          </div>
        )}
      </div>
    );
  }
  
  // Full Variant
  return (
    <div className="space-y-4">
      {/* Scraping Info */}
      {(metadata.scrapedAt || metadata.scrapedByUsername) && (
        <div>
          <h4 className="text-sm font-medium mb-2">Scraping Information</h4>
          <div className="grid grid-cols-2 gap-3">
            {metadata.scrapedAt && (
              <div className="flex items-start gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm font-medium">Scraped At</p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(metadata.scrapedAt).toLocaleString()}
                  </p>
                </div>
              </div>
            )}
            
            {metadata.scrapedByUsername && (
              <div className="flex items-start gap-2">
                <User className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm font-medium">Scraped By</p>
                  <p className="text-xs text-muted-foreground">
                    {metadata.scrapedByUsername}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Context Info */}
      {(metadata.sourceContext || metadata.scrapingMode) && (
        <>
          <Separator />
          <div>
            <h4 className="text-sm font-medium mb-2">Context</h4>
            <div className="flex gap-2">
              {metadata.sourceContext && (
                <Badge variant="outline">
                  Source: {metadata.sourceContext}
                </Badge>
              )}
              {metadata.scrapingMode && (
                <Badge variant="secondary">
                  Mode: {metadata.scrapingMode}
                </Badge>
              )}
            </div>
          </div>
        </>
      )}
      
      {/* LinkedIn Details */}
      {(metadata.groupName || metadata.companyName || metadata.eventName) && (
        <>
          <Separator />
          <div>
            <h4 className="text-sm font-medium mb-2">LinkedIn Details</h4>
            <div className="space-y-2">
              {metadata.groupName && (
                <div className="flex items-start gap-2">
                  <Users2 className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Group</p>
                    <p className="text-xs text-muted-foreground">
                      {metadata.groupName}
                      {metadata.groupId && ` (ID: ${metadata.groupId})`}
                    </p>
                  </div>
                </div>
              )}
              
              {metadata.companyName && (
                <div className="flex items-start gap-2">
                  <Building2 className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Company</p>
                    <p className="text-xs text-muted-foreground">
                      {metadata.companyName}
                      {metadata.companyId && ` (ID: ${metadata.companyId})`}
                    </p>
                  </div>
                </div>
              )}
              
              {metadata.eventName && (
                <div className="flex items-start gap-2">
                  <CalendarDays className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Event</p>
                    <p className="text-xs text-muted-foreground">
                      {metadata.eventName}
                      {metadata.eventId && ` (ID: ${metadata.eventId})`}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
      
      {/* Statistics */}
      {(metadata.profileCount || metadata.pagesScraped) && (
        <>
          <Separator />
          <div>
            <h4 className="text-sm font-medium mb-2">Statistics</h4>
            <div className="grid grid-cols-2 gap-3">
              {metadata.profileCount && (
                <div className="flex items-start gap-2">
                  <TrendingUp className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Profiles</p>
                    <p className="text-xs text-muted-foreground">
                      {metadata.profileCount} captured
                    </p>
                  </div>
                </div>
              )}
              
              {metadata.pagesScraped && (
                <div className="flex items-start gap-2">
                  <Hash className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Pages</p>
                    <p className="text-xs text-muted-foreground">
                      {metadata.pagesScraped} scraped
                      {metadata.scrapingCompleted && ' (completed)'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
      
      {/* Notes */}
      {metadata.notes && (
        <>
          <Separator />
          <div>
            <h4 className="text-sm font-medium mb-2">Notes</h4>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">
              {metadata.notes}
            </p>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Format relative date
 */
function formatRelativeDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}